import React, { memo, useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { Upload, Row, Col } from 'antd';
import { Spin } from 'antd5';
import { UploadChangeParam } from 'antd/lib/upload';
import { CloudUpload, Description, Delete } from '@material-ui/icons';
import { useMutation } from 'react-query';
import produce from 'immer';
import { validate as uuidValidate } from 'uuid';
import { LoadingOutlined } from '@ant-design/icons';

import { OnChangeCallback } from '.';

import { Button, Alert } from '../';
import theme from '../../theme';
import { uploadDocument, deleteDocument, getDocuments, getDocument } from '../../apis/users/documents';
import { IDocument, DocumentType } from '../../types/models/document';

export type FileUploadProps = {
  name?: string;
  value?: IDocument[];
  documentType?: DocumentType;
  maxFileSize?: number;
  maxUploadLimit?: number;
  accept?: string;
  disabled?: boolean;
  onChange?: OnChangeCallback;
  pullFromDocBank?: boolean;
  panelName?: string;
  readFile?: boolean;
  setMultiDocUploadInProgress?: (state: boolean) => void;
  multiDocUploadInProgress?: boolean;
};

type FileItemProps = {
  id: string;
  name: string;
  disabled?: boolean;
  onDelete: (id: string) => void;
  onError: (message: string) => void;
  setMultipleDocUploadInProgress: (state: boolean) => void;
};

type UploadedItem = IDocument;

export const MB_MULTIPLIER = 1024 * 1024;
const DEFAULT_MAX_UPLOAD_FILE_SIZE = MB_MULTIPLIER * 300; // 300 MB
const DEFAULT_ACCEPT = ['.png', '.jpg', '.jpeg', '.pdf', '.docx', '.doc', '.mp4', '.mov'].join(',');
const DEFAULT_UPLOAD_PROPS = {
  multiple: false,
  showUploadList: false,
  beforeUpload: () => false,
};

const UploadWrapper = styled(Upload)`
  text-align: center;
  display: block;
  margin-bottom: 16px;
  cursor: pointer;

  .ant-upload.ant-upload-select {
    display: block;
  }
`;

const UploadContainer = styled.div`
  width: 100%;
  border-radius: 4px;
  background-color: ${theme.colorsLightGrey};
  padding: 36px 36px 24px;
`;

const StyledSpin = styled(Spin)`
  margin-bottom: 7px;
`;

const SpinIconStyles = styled(LoadingOutlined)`
  color: ${theme.colorsBlack} !important;
`;

const UploadIconStyles = styled(CloudUpload)`
  color: ${theme.colorsBlack};
`;

const UploadText = styled.span`
  color: ${theme.colorsBlack};
  font-size: 16px;
  font-weight: bolder;
`;

const FileFormats = styled.div`
  margin-top: 12px;
`;

const AlertStyles = styled(Alert)`
  margin-bottom: 16px;
`;

const FileNameText = styled.div`
  flex: 1;
  color: ${theme.colorsSecondaryBlue};
  font-size: 14px;
  line-height: normal;
  padding: 0px 14px;
  word-break: break-all;
  text-decoration: underline;
  &:hover {
    font-weight: bold;
    border: none;
    box-shadow: none;
    cursor: pointer;
  }
`;

const PullFromBankButton = styled(Button)`
  &.ant-btn {
    font-size: 14px !important;
  }
`;

const DescriptionIconStyles = styled(Description)`
  color: ${theme.colorsBlack};
`;

const DeleteIconStyles = styled(Delete)`
  color: ${theme.colorsBlack};
  cursor: pointer;
`;

const FileItemRow = styled(Row)`
  margin-bottom: 8px;
`;

const PullFromBankBlock = styled(Row)`
  margin-top: 8px;
`;

const transformDocumentToUploadedItem = (document: IDocument): UploadedItem => ({ ...document });
const transformUploadedItemToDocument = (uploadedItem: UploadedItem): IDocument => ({ ...uploadedItem });

const FileItem = memo(function FileItem({ id, name, disabled = false, onDelete, onError }: FileItemProps) {
  const [isDeleteInProgress, setDeleteInProgress] = useState<boolean>(false);

  const { mutateAsync: deleteFile } = useMutation<void, unknown, { id: string }>(({ id }) => deleteDocument(id));

  const onDeleteClickHandler = useCallback(async () => {
    setDeleteInProgress(true);

    try {
      await deleteFile({ id });
      onDelete(id);
    } catch (err: any) {
      // File was not found, so remove it from uploaded items and re-save the form
      if (err.response.status === 404) {
        onDelete(id);
      } else {
        onError(err.message);
      }
    } finally {
      setDeleteInProgress(false);
    }
  }, [deleteFile, id, onDelete, onError]);

  /**
   * Handle download the document in the document bank
   */
  const onDownload = useCallback(
    async (id: string, fileName: string) => {
      try {
        const file: File = await getDocument(id, true);
        const objectUrl = window.URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err: any) {
        onError(err.message);
      }
    },
    [onError]
  );

  return (
    <FileItemRow justify="space-between" align="middle">
      <DescriptionIconStyles titleAccess="Document" />
      <FileNameText onClick={() => onDownload(id, name)} title={name}>
        {name}
      </FileNameText>
      {!disabled && (
        <>
          {isDeleteInProgress && <Spin indicator={<SpinIconStyles spin />} />}
          {!isDeleteInProgress && <DeleteIconStyles titleAccess="Delete document" onClick={onDeleteClickHandler} />}
        </>
      )}
    </FileItemRow>
  );
});

const DOCUMENT_MISSING_ERROR_MESSAGE =
  'This document doesn’t exist in your document bank. Please upload a new document.';

const FileUpload = memo(function FileUpload({
  name,
  value,
  documentType,
  maxFileSize = DEFAULT_MAX_UPLOAD_FILE_SIZE,
  maxUploadLimit = 1,
  accept = DEFAULT_ACCEPT,
  disabled = false,
  onChange,
  pullFromDocBank = false,
  panelName = '',
  readFile = false,
  multiDocUploadInProgress = false,
  setMultiDocUploadInProgress,
}: FileUploadProps) {
  // states
  const [uploadedItems, setUploadedItems] = useState<UploadedItem[]>(value || []);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isUploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [isPullFromDocumentBankInProcess, setPullFromDocumentBankInProcess] = useState<boolean>(false);
  const [stageId, setStageId] = useState<string>('');
  // api
  const { mutateAsync: uploadFile } = useMutation<IDocument, unknown, File>((file) =>
    uploadDocument(file, documentType, stageId)
  );

  useEffect(() => {
    const pathName = window.location.pathname.split('/');
    const stageId = pathName[pathName.length - 1];
    if (uuidValidate(stageId)) {
      setStageId(pathName[pathName.length - 1]);
    }
  }, []);

  //update list of upload files when changed page
  useEffect(() => {
    if (value) {
      setUploadedItems(value.map(transformDocumentToUploadedItem));
    }
  }, [name, documentType, panelName, value]);

  // helper methods
  const updateUploadedItems = useCallback(
    (uploadedItems: UploadedItem[]) => {
      setUploadedItems(uploadedItems);

      if (onChange) {
        const value = uploadedItems.length > 0 ? uploadedItems.map(transformUploadedItemToDocument) : undefined;
        onChange({ name, value });
      }
    },
    [name, onChange]
  );

  const setMultipleDocUploadInProgress = useCallback(
    (state: boolean) => {
      if (setMultiDocUploadInProgress) {
        setMultiDocUploadInProgress(state);
      }
    },
    [setMultiDocUploadInProgress]
  );

  const validate = useCallback(
    (file) => {
      const fileExtension = file.name?.split('.')?.pop()?.toLocaleLowerCase();
      const isFileTooLarge = file.size > maxFileSize;
      let validationResult = true;

      if (!accept.includes(fileExtension)) {
        setErrorMessage(`The file format must be ${accept.replace(/\./g, ' ').toUpperCase()}.`);
        validationResult = false;
      } else if (isFileTooLarge) {
        setErrorMessage(`Your file size is too large, it exceeds ${maxFileSize / MB_MULTIPLIER} MB.`);
        validationResult = false;
      }

      return validationResult;
    },
    [accept, maxFileSize]
  );

  // handlers
  const onChangeHandler = useCallback(
    async ({ file }: UploadChangeParam<any>) => {
      if (validate(file)) {
        setErrorMessage('');
        setUploadInProgress(true);
        setMultipleDocUploadInProgress(true);

        try {
          const document: IDocument = await uploadFile(file);
          updateUploadedItems(
            produce(uploadedItems, (draftItems) => {
              draftItems.push(transformDocumentToUploadedItem(document));
            })
          );
        } catch (err: any) {
          setErrorMessage(err.message);
        } finally {
          setUploadInProgress(false);
          setMultipleDocUploadInProgress(false);
        }
      }
    },
    [validate, uploadFile, updateUploadedItems, uploadedItems, setMultipleDocUploadInProgress]
  );

  const onChangeHandlerReadFile = useCallback(
    async ({ file }: UploadChangeParam<any>) => {
      if (!validate(file)) {
        setErrorMessage('File is not supported');
        return;
      }

      setErrorMessage('');
      setUploadInProgress(true);
      setMultipleDocUploadInProgress(true);

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e?.target?.result as string | null | undefined;

        if (!result) {
          setErrorMessage('Error reading file');
          setUploadInProgress(false);
          setMultipleDocUploadInProgress(false);
          return;
        }

        if (onChange) {
          onChange({ name, value: [result] });
        }
        setUploadInProgress(false);
        setMultipleDocUploadInProgress(false);
      };

      reader.onerror = () => {
        setErrorMessage('Error reading file');
        setUploadInProgress(false);
        setMultipleDocUploadInProgress(false);
      };

      reader.readAsText(file);
    },
    [validate, onChange, name, setMultipleDocUploadInProgress]
  );

  const onDocumentDeleteHandler = useCallback(
    (documentId: string) => {
      setErrorMessage('');
      updateUploadedItems(produce(uploadedItems, (draftItems) => draftItems.filter(({ id }) => id !== documentId)));
    },
    [updateUploadedItems, uploadedItems]
  );

  const onDocumentErrorHandler = useCallback((message: string) => {
    setErrorMessage(message);
  }, []);

  const onAlertCloseHandler = useCallback(() => {
    setErrorMessage('');
  }, []);

  const isUploadDisabled =
    multiDocUploadInProgress ||
    isUploadInProgress ||
    uploadedItems.length >= maxUploadLimit ||
    disabled ||
    isPullFromDocumentBankInProcess;
  const canShowUploader = uploadedItems.length < maxUploadLimit;

  /**
   * Handle pull document from the document bank
   */
  const handlePullFromBank = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation(); //prevent the event bubbling and only fire the event handler of the link button
      const documentTypeFilter = (document: IDocument) => {
        return documentType && document.documentType === documentType;
      };
      try {
        setPullFromDocumentBankInProcess(true);
        const documentList: IDocument[] = await getDocuments();
        const existingDocuments: IDocument[] = documentList.filter(documentTypeFilter);

        if (existingDocuments.length === 0) {
          throw Error(DOCUMENT_MISSING_ERROR_MESSAGE);
        }
        const existingDocument = existingDocuments[0];
        const file: File = await getDocument(existingDocument.id, true);
        const newDocument: IDocument = await uploadFile(file);
        updateUploadedItems(
          produce(uploadedItems, (draftItems) => {
            draftItems.push(transformDocumentToUploadedItem(newDocument));
          })
        );
      } catch (err: any) {
        setErrorMessage(err.message);
      } finally {
        setPullFromDocumentBankInProcess(false);
      }
    },
    [documentType, updateUploadedItems, uploadFile, uploadedItems]
  );

  return (
    <div>
      {canShowUploader && (
        <UploadWrapper
          {...DEFAULT_UPLOAD_PROPS}
          accept={accept}
          onChange={readFile ? onChangeHandlerReadFile : onChangeHandler}
          disabled={isUploadDisabled}
        >
          <UploadContainer>
            {(isUploadInProgress || multiDocUploadInProgress) && (
              <StyledSpin indicator={<SpinIconStyles type="loading" spin />} />
            )}
            {!isUploadInProgress && !multiDocUploadInProgress && <UploadIconStyles />}
            <div>
              <UploadText>Click or drag file to this area to upload</UploadText>
              {pullFromDocBank && documentType && (
                <PullFromBankBlock justify="center" gutter={[8, 0]}>
                  <Col>or</Col>
                  <Col>
                    <PullFromBankButton type="link" disabled={isUploadDisabled} onClick={handlePullFromBank}>
                      Pull from Document Bank
                    </PullFromBankButton>
                  </Col>
                </PullFromBankBlock>
              )}
              <FileFormats>File formats: {accept.replace(/\./g, ' ').toUpperCase()}</FileFormats>
              <div>{`Max file size ${maxFileSize / MB_MULTIPLIER} MB`}</div>
            </div>
          </UploadContainer>
        </UploadWrapper>
      )}
      {errorMessage && <AlertStyles message={errorMessage} type="error" onClose={onAlertCloseHandler} />}
      {uploadedItems.map(({ id, fileName }, i) => {
        return (
          <FileItem
            key={`${i}-${id}`}
            id={id}
            name={fileName}
            onDelete={onDocumentDeleteHandler}
            onError={onDocumentErrorHandler}
            disabled={disabled}
            setMultipleDocUploadInProgress={setMultipleDocUploadInProgress}
          />
        );
      })}
    </div>
  );
});

export default FileUpload;
